<script setup lang="ts">
import ReportTemplateMenu from '@/components/createdUi/ReportTemplateMenu.vue'
import { reportFilters } from '@/modules/reportTemplates/filters'

/*
* Компонент AuditNavigationToolbar - Панель навигации для модуля аудитов
*
* Основные функции:
* - Переход между разделами аудитов (реестр, календарь, группы, статистика и т.д.)
* - Создание новых аудитов и групп аудитов
* - Управление видимостью кнопок в зависимости от текущего маршрута
*/

// Импорт хуков маршрутизации
import { useRoute, useRouter } from 'vue-router'
// Импорт хука определения размера экрана
import { useDisplay } from 'vuetify/framework'
// Импорт перечисления названий маршрутов
import { ERouteNames } from '@/router/ERouteNames'
import OnboardingLaunchButton from '@/modules/onboarding/components/OnboardingLaunchButton.vue'
import { useOnboardingContext } from '@/modules/onboarding/composables/useOnboardingContext'
import { AUDITS_ANCHORS, AUDITS_CONTEXTS } from '@/modules/onboarding/scenarios/audits/anchors'
// Импорт хранилища состояния загрузки и обратной связи
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
// Импорт хранилища групп аудитов
import { useAuditGroupStore } from '@/modules/audits/stores/useAuditGroupStore/useAuditGroupStore'
// Импорт хранилища реестра аудитов
import { useAuditRegisterStore } from '@/modules/audits/stores/useAuditRegisterStore/useAuditRegisterStore'
// Импорт хранилища графиков (не используется напрямую, но оставлено для совместимости)
import { useGraphStore } from '@/modules/dashboard/stores/graphStore'

// Инициализация маршрутизатора
const router = useRouter()
// Инициализация текущего маршрута
const route = useRoute()
// Инициализация хранилища групп аудитов
const auditGroupStore = useAuditGroupStore()
// Инициализация хранилища реестра аудитов
const auditRegisterStore = useAuditRegisterStore()
// Инициализация хранилища графиков (не используется напрямую, но оставлено для совместимости)
const graphStore = useGraphStore()

// Определение текущего размера экрана (для мобильной адаптации)
const { xs, smAndDown } = useDisplay()

/** На мобилке — индекс открытой панели «Навигация по разделам»; undefined = свёрнута */
const navPanelsOpen = ref<number>()

// Кнопка «Обучение» запускает сценарий текущего раздела аудита
const ONBOARDING_SCENARIOS: Partial<Record<ERouteNames, string>> = {
  [ERouteNames.AUDITS]: 'audits',
  [ERouteNames.AUDITS_NONCONFORMITIES]: 'audits.nonconformities',
  [ERouteNames.AUDITS_GROUP]: 'audits.groups',
  [ERouteNames.AUDITS_ARCHIVE]: 'audits.archive',
  [ERouteNames.AUDITS_CHART]: 'audits.chart',
}

const onboardingScenario = computed(() => ONBOARDING_SCENARIOS[route.name as ERouteNames])

useOnboardingContext({
  names: [AUDITS_CONTEXTS.toolbar],
  root: () => document.body,
  actions: {
    openNavPanel: () => {
      if (xs.value)
        navPanelsOpen.value = 0
    },
  },
})

const collapseNavPanel = () => {
  navPanelsOpen.value = undefined
}

// Функции перехода на различные страницы модуля аудитов
const goToAuditsNonconformities = () => {
  collapseNavPanel()
  router.push({ name: ERouteNames.AUDITS_NONCONFORMITIES })
}
const goToAuditsStatistics = () => {
  collapseNavPanel()
  router.push({ name: ERouteNames.AUDITS_GRAPH })
}
const goToAuditsArchive = () => {
  collapseNavPanel()
  router.push({ name: ERouteNames.AUDITS_ARCHIVE })
}
const goToAuditsCharts = () => {
  collapseNavPanel()
  router.push({ name: ERouteNames.AUDITS_CHART })
}
const goToAuditsRegister = () => {
  collapseNavPanel()
  router.push({ name: ERouteNames.AUDITS })
}
const goToAuditsGroup = () => {
  collapseNavPanel()
  router.push({ name: ERouteNames.AUDITS_GROUP })
}

// Вычисляемое свойство: текущее имя маршрута
const currentRouteName = computed(() => router.currentRoute.value.name)
// Инициализация хранилища обратной связи (глобальная загрузка)
const feedbackStore = useFeedbackStore()

// Вычисляемое свойство: скрывать ли панель навигации (например, внутри детального просмотра аудита)
const hideToolbar = computed(() => {
  if (!route.name) return false

  return (
    route.name === ERouteNames.AUDIT_DETAIL
    || route.name === ERouteNames.AUDIT_PROCESS
    || route.name === ERouteNames.AUDIT_NONCONFORMITIES
    || route.name === ERouteNames.AUDIT_NONCONFORMITY_DETAIL
  )
})

// Метод проверки скрыта ли кнопка для текущего маршрута
const isHiddenButton = (name: ERouteNames | ERouteNames[]) => {
  if (Array.isArray(name)) {
    return name.includes(currentRouteName.value as ERouteNames)
  }

  return currentRouteName.value === name
}

// Метод проверки активна ли кнопка для текущего маршрута
const isActiveButton = (name: ERouteNames | ERouteNames[]) => {
  if (Array.isArray(name)) {
    return name.includes(currentRouteName.value as ERouteNames)
  }

  return currentRouteName.value === name
}

// Метод создания нового аудита (сбрасывает состояние перед переходом)
const createAudit = () => {
  auditRegisterStore.resetAudit()
  auditGroupStore.setEmptyGroup()
  router.push({ name: ERouteNames.AUDIT_CREATE })
}

// Асинхронный метод создания новой группы аудитов
const createAuditGroup = async () => {
  auditGroupStore.setEmptyGroup()
  await router.push({ name: ERouteNames.AUDIT_GROUP_CREATE })
}
const templateFilters = computed(() => reportFilters(auditRegisterStore.filters))
</script>

<template>
  <div
    v-if="!hideToolbar"
    class="d-flex flex-column"
    :class="smAndDown ? 'w-100 ga-2' : ''"
  >
    <div class="d-flex align-center gap-2" :class="xs ? 'w-100 flex-column' : 'justify-space-between'">
    <div class="d-flex align-center gap-2" :class="{ 'w-100': xs }">
      <VBtn
        v-if="isHiddenButton(ERouteNames.AUDITS)"
        :class="{ 'w-100': xs }"
        variant="elevated"
        :loading="feedbackStore.isGlobalLoading"
        prepend-icon="ri-add-line"
        class="general-button"
        :data-onboarding="AUDITS_ANCHORS.toolbar.createAudit"
        @click="createAudit"
      >
        Создать аудит
      </VBtn>

      <VBtn
        v-if="isHiddenButton([
          ERouteNames.AUDITS_GROUP,
          ERouteNames.AUDIT_GROUP_CREATE,
          ERouteNames.AUDIT_GROUP_DETAIL,
        ])"
        :class="{ 'w-100': xs }"
        variant="elevated"
        :loading="feedbackStore.isGlobalLoading"
        prepend-icon="ri-add-line"
        class="general-button"
        :data-onboarding="AUDITS_ANCHORS.toolbar.createGroup"
        @click="createAuditGroup"
      >
        Создать группу
      </VBtn>
    </div>

    <!-- На мобильных скрываем кнопки навигации в Expansion Panel -->
    <VExpansionPanels v-if="xs" v-model="navPanelsOpen" class="w-100 mt-2">
      <VExpansionPanel>
        <VExpansionPanelTitle class="d-flex align-center py-2 px-4" style="min-height: 48px;">
          <div class="d-flex align-center gap-2">
            <VIcon icon="ri-menu-2-line" size="20" />
            <span class="text-subtitle-1 font-weight-medium">Навигация по разделам</span>
          </div>
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          <div class="d-flex flex-column gap-2 w-100">
            <VBtn
              class="w-100"
              :active="isActiveButton(ERouteNames.AUDITS)"
              variant="outlined"
              color="secondary"
              prepend-icon="ri-bill-line"
              :data-onboarding="AUDITS_ANCHORS.toolbar.navFirst"
              @click="goToAuditsRegister"
            >
              Реестр
            </VBtn>

            <VBtn
              class="w-100"
              :active="isActiveButton([ERouteNames.AUDITS_GROUP, ERouteNames.AUDIT_GROUP_CREATE, ERouteNames.AUDIT_GROUP_DETAIL])"
              variant="outlined"
              color="secondary"
              prepend-icon="ri-team-line"
              @click="goToAuditsGroup"
            >
              Группы
            </VBtn>

            <VBtn
              class="w-100"
              :active="isActiveButton(ERouteNames.AUDITS_NONCONFORMITIES)"
              variant="outlined"
              color="secondary"
              prepend-icon="ri-bill-line"
              @click="goToAuditsNonconformities"
            >
              Несоответствия
            </VBtn>

            <VBtn
              class="w-100"
              :active="isActiveButton(ERouteNames.AUDITS_GRAPH)"
              variant="outlined"
              color="secondary"
              prepend-icon="ri-pie-chart-line"
              @click="goToAuditsStatistics"
            >
              Статистика
            </VBtn>

            <VBtn
              class="w-100"
              :active="isActiveButton(ERouteNames.AUDITS_CHART)"
              variant="outlined"
              color="secondary"
              prepend-icon="ri-bar-chart-horizontal-line"
              @click="goToAuditsCharts"
            >
              График
            </VBtn>

            <VBtn
              class="w-100"
              :active="isActiveButton(ERouteNames.AUDITS_ARCHIVE)"
              variant="outlined"
              color="secondary"
              prepend-icon="ri-archive-line"
              :data-onboarding="AUDITS_ANCHORS.toolbar.navLast"
              @click="goToAuditsArchive"
            >
              Архив
            </VBtn>
            <ReportTemplateMenu v-if="currentRouteName === ERouteNames.AUDITS" object-type="audit" context="collection" :filters="templateFilters" :sort="auditRegisterStore.currentSortString" variant="toolbar" />
            <div class="d-flex justify-end">
              <OnboardingLaunchButton :scenario="onboardingScenario" />
            </div>
          </div>
        </VExpansionPanelText>
      </VExpansionPanel>
    </VExpansionPanels>

    <!-- На ПК показываем обычный горизонтальный ряд кнопок -->
    <div v-else class="d-flex align-center gap-2">
      <VBtn
        :active="isActiveButton(ERouteNames.AUDITS)"
        variant="outlined"
        color="secondary"
        prepend-icon="ri-bill-line"
        :data-onboarding="AUDITS_ANCHORS.toolbar.navFirst"
        @click="goToAuditsRegister"
      >
        Реестр
      </VBtn>

      <VBtn
        :active="isActiveButton([ERouteNames.AUDITS_GROUP, ERouteNames.AUDIT_GROUP_CREATE, ERouteNames.AUDIT_GROUP_DETAIL])"
        variant="outlined"
        color="secondary"
        prepend-icon="ri-team-line"
        @click="goToAuditsGroup"
      >
        Группы
      </VBtn>

      <VBtn
        :active="isActiveButton(ERouteNames.AUDITS_NONCONFORMITIES)"
        variant="outlined"
        color="secondary"
        prepend-icon="ri-bill-line"
        @click="goToAuditsNonconformities"
      >
        Несоответствия
      </VBtn>

      <VBtn
        :active="isActiveButton(ERouteNames.AUDITS_GRAPH)"
        variant="outlined"
        color="secondary"
        prepend-icon="ri-pie-chart-line"
        @click="goToAuditsStatistics"
      >
        Статистика
      </VBtn>

      <VBtn
        :active="isActiveButton(ERouteNames.AUDITS_CHART)"
        variant="outlined"
        color="secondary"
        prepend-icon="ri-bar-chart-horizontal-line"
        @click="goToAuditsCharts"
      >
        График
      </VBtn>

      <VBtn
        :active="isActiveButton(ERouteNames.AUDITS_ARCHIVE)"
        variant="outlined"
        color="secondary"
        prepend-icon="ri-archive-line"
        :data-onboarding="AUDITS_ANCHORS.toolbar.navLast"
        @click="goToAuditsArchive"
      >
        Архив
      </VBtn>
      <ReportTemplateMenu v-if="currentRouteName === ERouteNames.AUDITS" object-type="audit" context="collection" :filters="templateFilters" :sort="auditRegisterStore.currentSortString" variant="toolbar" />
      <OnboardingLaunchButton :scenario="onboardingScenario" />
    </div>
    </div>

    <!-- Таблицы аудитов телепортируют сюда панель фильтров на мобилке -->
    <div v-if="smAndDown" id="audit-mobile-filters" class="w-100" />
  </div>
</template>

<style scoped lang="scss">
:deep(.v-expansion-panel-text__wrapper) {
  padding: 12px 16px 16px 16px !important;
}
</style>
